import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plane,
  Ship,
  ArrowLeft,
  Loader2,
  ShieldCheck,
  AlertCircle,
  CheckCircle2,
  Package,
  MapPin,
  Calendar,
  Building2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import TraceabilityTimeline from './components/TraceabilityTimeline';
import { verifyPublicTraceability } from './Services/traceabilityService';

/**
 * Página PÚBLICA de trazabilidad.
 *
 * Rutas: /t/:operador/:numero  (links nuevos)
 *        /t/:numero            (links enviados antes de agregar el operador)
 *
 * El operador importa: la trazabilidad vive en la base de cada operador
 * y el número se repite entre ellos. Sin él, el backend la busca en
 * todos y el NIT decide.
 *
 * Flujo:
 *  1) El cliente entra al link del email → ve el número
 *     pre-rellenado pero debe escribir su NIT.
 *  2) Se llama a POST /public/traceability/verify.
 *  3) Si el NIT coincide, se muestra la línea de tiempo.
 *  4) Si no, mensaje de error.
 *
 * Es una página sin auth (no usa Layout ni Navbar).
 */
export default function TraceabilityPublicPage() {
  const { numero = '', operador = '' } = useParams();
  const decodedNumero = decodeURIComponent(numero);
  const decodedOperador = decodeURIComponent(operador);

  const [nit, setNit] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [alreadyVerified, setAlreadyVerified] = useState(false);

  // Si por alguna razón el usuario entra a la URL con ?nit=XXX
  // y el back ya tiene el DTO cacheado, podríamos reusar.
  // Por ahora, siempre pedimos el NIT.
  useEffect(() => {
    setData(null);
    setError(null);
    setNit('');
    setAlreadyVerified(false);
  }, [decodedNumero]);

  const onSubmit = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      if (!nit.trim() || !decodedNumero) return;
      setSubmitting(true);
      setError(null);
      try {
        const r = await verifyPublicTraceability({
          numero: decodedNumero,
          nit: nit.trim(),
          operador: decodedOperador || undefined,
        });
        if (!r?.success) {
          setError(r?.message || 'No se pudo verificar la trazabilidad');
          return;
        }
        setData(r.data);
        setAlreadyVerified(true);
      } catch (err) {
        setError(err?.message || 'Error al verificar la trazabilidad');
      } finally {
        setSubmitting(false);
      }
    },
    [decodedNumero, decodedOperador, nit],
  );

  return (
    <div className="relative min-h-screen">
      {/* Fondo full-bleed */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-white via-indigo-50 to-slate-50 dark:from-slate-900 dark:via-indigo-900 dark:to-slate-800" />
        <div className="absolute -top-32 -right-20 h-72 w-72 rounded-full bg-indigo-400/20 dark:bg-indigo-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-fuchsia-400/10 dark:bg-fuchsia-500/10 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(rgba(0,0,0,0.03)_1px,_transparent_1px)] dark:bg-[radial-gradient(transparent_1px,_rgba(255,255,255,0.04)_1px)] [background-size:16px_16px]" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header compacto */}
        <header className="px-4 sm:px-6 py-4 border-b bg-white/70 dark:bg-white/5 backdrop-blur">
          <div className="container mx-auto max-w-4xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              {data?.operator?.logo ? (
                <img
                  src={data.operator.logo}
                  alt={data.operator.nombre}
                  className="h-9 w-9 object-contain rounded"
                />
              ) : (
                <div className="bg-primary/10 p-2 rounded-lg">
                  {/* El modulo define el icono. Sin `modulo` (docs
                      previos al LCL) se mantiene el aereo. */}
                  {data?.modulo === 'lcl' ? (
                    <Ship className="h-5 w-5 text-primary" />
                  ) : (
                    <Plane className="h-5 w-5 text-primary" />
                  )}
                </div>
              )}
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {data?.operator?.nombre || 'OptyCargo'}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Portal de trazabilidad
                </p>
              </div>
            </div>
            {alreadyVerified && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setData(null);
                  setAlreadyVerified(false);
                }}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Cambiar NIT
              </Button>
            )}
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 py-8">
          <div className="container mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {!alreadyVerified ? (
                <VerifyCard
                  numero={decodedNumero}
                  nit={nit}
                  setNit={setNit}
                  onSubmit={onSubmit}
                  submitting={submitting}
                  error={error}
                />
              ) : data ? (
                <TraceabilityView data={data} />
              ) : null}
            </motion.div>
          </div>
        </main>

        <footer className="px-4 sm:px-6 py-4 text-center text-[11px] text-muted-foreground">
          © {new Date().getFullYear()} {data?.operator?.nombre || 'OptyCargo'}. Todos los derechos reservados.
        </footer>
      </div>
    </div>
  );
}

function VerifyCard({ numero, nit, setNit, onSubmit, submitting, error }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="bg-primary/10 p-2 rounded-lg">
            <ShieldCheck className="h-6 w-6 text-primary" />
          </div>
          <div>
            <CardTitle className="text-xl">Consulta tu trazabilidad</CardTitle>
            <CardDescription>
              Ingresa tu NIT para ver el estado de tu operación.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Número de trazabilidad</Label>
            <Input
              value={numero}
              readOnly
              className="font-mono bg-muted/30"
            />
            <p className="text-[11px] text-muted-foreground">
              Este número lo encuentras en el correo que te enviamos.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="nit">NIT de la empresa</Label>
            <Input
              id="nit"
              autoFocus
              placeholder="Ej. 900123456-7"
              value={nit}
              onChange={(e) => setNit(e.target.value)}
              required
            />
          </div>

          {error ? (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Verificando…
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Ver trazabilidad
              </>
            )}
          </Button>

          <p className="text-[11px] text-muted-foreground text-center">
            ¿Problemas para acceder? Responde al correo que te enviamos y te ayudamos.
          </p>
        </form>
      </CardContent>
    </Card>
  );
}

function TraceabilityView({ data }) {
  // Snapshot de la solicitud segun el modulo. El aereo sigue
  // leyendo `airRequestSnapshot` exactamente como antes.
  const ar = data.airRequestSnapshot || data.lclRequestSnapshot || {};
  const esLcl = data.modulo === 'lcl';
  const cliente = data.clienteSnapshot || {};
  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary" className="font-mono text-xs">
                  {data.numero}
                </Badge>
                <Badge
                  variant="outline"
                  className={
                    data.estado === 'Activa'
                      ? 'border-emerald-300 text-emerald-700'
                      : data.estado === 'Completada'
                      ? 'border-blue-300 text-blue-700'
                      : 'border-rose-300 text-rose-700'
                  }
                >
                  {data.estado}
                </Badge>
              </div>
              <CardTitle className="text-xl mt-2">
                Tu operación con {data.operator?.nombre || 'el operador'}
              </CardTitle>
              <CardDescription>
                Hola {cliente.nombre}, aquí puedes ver el avance en tiempo real.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${
              esLcl ? 'lg:grid-cols-5' : 'lg:grid-cols-4'
            }`}
          >
            <Stat
              icon={Package}
              label="Solicitud"
              value={ar.codigo || '—'}
              sub="Código interno"
            />
            <Stat
              icon={MapPin}
              label="Ruta"
              value={
                ar.origen && ar.destino
                  ? `${ar.origen} → ${ar.destino}`
                  : '—'
              }
            />
            <Stat
              icon={Building2}
              label="Operador"
              value={data.operator?.nombre || '—'}
            />
            <Stat
              icon={Calendar}
              label="Registrada"
              value={
                ar.createdAt
                  ? new Date(ar.createdAt).toLocaleDateString('es-CO', {
                      year: 'numeric',
                      month: 'short',
                      day: '2-digit',
                    })
                  : '—'
              }
            />
            {esLcl && (
              <Stat
                icon={Package}
                label="Carga"
                value={
                  ar.volumenTotal != null
                    ? `${Number(ar.volumenTotal).toFixed(3)} m³`
                    : '—'
                }
                sub={
                  [
                    ar.numPiezas != null ? `${ar.numPiezas} bultos` : null,
                    ar.pesoTotal != null ? `${Number(ar.pesoTotal).toFixed(2)} kg` : null,
                    ar.unidadesWM != null
                      ? `${Number(ar.unidadesWM).toFixed(3)} W/M`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || null
                }
              />
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Línea de tiempo</CardTitle>
          <CardDescription>
            Cada hito es un evento registrado de tu operación. Te avisaremos por correo
            cuando haya novedades.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TraceabilityTimeline hitos={data.hitos} mode="public" />
        </CardContent>
      </Card>
    </>
  );
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-md border bg-muted/20 p-3 space-y-0.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="text-sm font-medium text-foreground break-words">{value}</div>
      {sub ? <div className="text-[11px] text-muted-foreground">{sub}</div> : null}
    </div>
  );
}
